import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Star, Shield, Zap, Crown, Users } from "lucide-react";

const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly"
  );
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const plans = [
    {
      id: "basic",
      name: "Basic",
      icon: <Shield className="w-8 h-8" />,
      description: "Perfect for recreational players",
      price: {
        monthly: 19,
        yearly: 190,
      },
      features: [
        "Basic impact protection",
        "Mobile app access",
        "Performance tracking",
        "Basic analytics",
        "Email support",
        "1-year warranty",
      ],
      notIncluded: [
        "AI-powered protection",
        "Real-time coaching",
        "Advanced analytics",
        "Priority support",
        "Team management",
      ],
      popular: false,
      color: "gray",
    },
    {
      id: "pro",
      name: "Pro",
      icon: <Zap className="w-8 h-8" />,
      description: "For serious athletes and competitive players",
      price: {
        monthly: 49,
        yearly: 490,
      },
      features: [
        "AI-powered adaptive protection",
        "Real-time performance analytics",
        "Injury prevention alerts",
        "Advanced coaching insights",
        "Priority support",
        "Custom training programs",
        "Social features",
        "2-year warranty",
      ],
      notIncluded: [
        "Team management dashboard",
        "Coach collaboration tools",
        "Custom branding",
      ],
      popular: true,
      color: "cresento-green",
    },
    {
      id: "elite",
      name: "Elite",
      icon: <Crown className="w-8 h-8" />,
      description: "For professional teams and academies",
      price: {
        monthly: 99,
        yearly: 990,
      },
      features: [
        "Everything in Pro",
        "Team management dashboard",
        "Coach collaboration tools",
        "Custom branding options",
        "Advanced team analytics",
        "Dedicated account manager",
        "Custom integrations",
        "Lifetime warranty",
      ],
      notIncluded: [],
      popular: false,
      color: "purple",
    },
  ];

  const addons = [
    {
      name: "Extra Charging Case",
      price: 49,
      description: "Wireless charging case for on-the-go power",
    },
    {
      name: "Premium Support",
      price: 19,
      description: "Priority phone and chat support",
    },
    {
      name: "Custom Branding",
      price: 99,
      description: "Your team logo and colors on the guards",
    },
    {
      name: "Extended Warranty",
      price: 79,
      description: "Extend warranty to 3 years with replacement coverage",
    },
  ];

  const faqs = [
    {
      question: "Is there a free trial?",
      answer:
        "Yes! We offer a 14-day free trial for all new customers. You can cancel anytime during the trial period.",
    },
    {
      question: "What's included with the hardware purchase?",
      answer:
        "Each Cresento Pro purchase includes the shin guards, charging case, mobile app access, and your chosen subscription plan for the first month.",
    },
    {
      question: "Can I switch plans anytime?",
      answer:
        "Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.",
    },
    {
      question: "Do you offer team discounts?",
      answer:
        "Yes, we offer significant discounts for teams of 10 or more players. Contact our sales team for custom pricing.",
    },
    {
      question: "What happens if I cancel my subscription?",
      answer:
        "You can continue using basic protection features, but advanced AI features and analytics will be disabled. Your data remains safe and can be reactivated anytime.",
    },
  ];

  const getColorClasses = (color: string, type: "bg" | "text" | "border") => {
    const colorMap = {
      gray: {
        bg: "bg-gray-600",
        text: "text-gray-600",
        border: "border-gray-600",
      },
      "cresento-green": {
        bg: "bg-cresento-green-600",
        text: "text-cresento-green-600",
        border: "border-cresento-green-600",
      },
      purple: {
        bg: "bg-purple-600",
        text: "text-purple-600",
        border: "border-purple-600",
      },
    };
    return colorMap[color as keyof typeof colorMap]?.[type] || "";
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-green-600 to-cresento-green-800 text-white py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Choose Your Plan
            </h1>
            <p className="text-xl md:text-2xl opacity-90">
              Get the protection and insights you need to perform at your best
            </p>
          </motion.div>
        </div>
      </section>

      {/* Billing Toggle */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center mb-8"
          >
            <div className="bg-cresento-primary/30 border border-cresento-secondary/30 rounded-full p-1 shadow-lg">
              <div className="flex">
                <button
                  onClick={() => setBillingPeriod("monthly")}
                  className={`px-6 py-3 rounded-full font-semibold transition-colors ${
                    billingPeriod === "monthly"
                      ? "bg-cresento-green-600 text-white"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod("yearly")}
                  className={`px-6 py-3 rounded-full font-semibold transition-colors relative ${
                    billingPeriod === "yearly"
                      ? "bg-cresento-green-600 text-white"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  Yearly
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hardware Purchase */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-white">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">
                    Cresento Pro Hardware
                  </h2>
                  <p className="text-lg mb-6 opacity-90">
                    One-time purchase of the physical shin guards with all
                    sensors and technology
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-cresento-green-400" />
                      <span>AI-powered shin guards</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-cresento-green-400" />
                      <span>Wireless charging case</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-cresento-green-400" />
                      <span>Mobile app access</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-cresento-green-400" />
                      <span>First month subscription included</span>
                    </li>
                  </ul>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-4">$299</div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-cresento-green-600 hover:bg-cresento-green-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
                  >
                    Pre-Order Now
                  </motion.button>
                  <p className="text-sm mt-4 opacity-70">Ships Q2 2024</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Subscription Plans */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-white"
          >
            Subscription Plans
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-cresento-primary/30 border border-cresento-secondary/30 rounded-2xl shadow-lg overflow-hidden relative ${
                  plan.popular ? "ring-2 ring-cresento-green-600" : ""
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-cresento-green-600 text-white text-center py-2 font-semibold">
                    Most Popular
                  </div>
                )}
                <div className={`p-8 ${plan.popular ? "pt-12" : ""}`}>
                  <div
                    className={`${getColorClasses(plan.color, "text")} mb-4`}
                  >
                    {plan.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-white">
                    {plan.name}
                  </h3>
                  <p className="text-gray-300 mb-6">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">
                      ${plan.price[billingPeriod]}
                    </span>
                    <span className="text-gray-400">
                      /{billingPeriod === "monthly" ? "month" : "year"}
                    </span>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? "bg-cresento-green-600 hover:bg-cresento-green-700 text-white"
                        : `border-2 ${getColorClasses(
                            plan.color,
                            "border"
                          )} ${getColorClasses(
                            plan.color,
                            "text"
                          )} hover:bg-cresento-primary/50`
                    }`}
                  >
                    {selectedPlan === plan.id ? "Selected" : "Choose Plan"}
                  </motion.button>
                </div>
                <div className="border-t border-cresento-secondary/30 p-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-cresento-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                    {plan.notIncluded.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-500">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20 bg-cresento-primary/20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-white"
          >
            Add-ons & Accessories
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {addons.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-cresento-primary/30 border border-cresento-secondary/30 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <h3 className="text-lg font-semibold mb-2 text-white">
                  {addon.name}
                </h3>
                <p className="text-gray-300 mb-4">{addon.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-white">
                    ${addon.price}
                  </span>
                  <button className="bg-cresento-green-600 hover:bg-cresento-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">
                    Add
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Pricing */}
      <section className="py-20 bg-cresento-primary/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Users className="w-16 h-16 text-cresento-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6 text-white">
              Team & Enterprise Pricing
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Special pricing for teams, academies, and organizations. Get bulk
              discounts and additional features.
            </p>
            <div className="bg-cresento-primary/30 border border-cresento-secondary/30 rounded-2xl p-8 shadow-lg">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-cresento-green-400 mb-2">
                    10+
                  </div>
                  <div className="text-sm text-gray-400">players</div>
                  <div className="text-lg font-semibold text-cresento-green-400">
                    15% off
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cresento-green-400 mb-2">
                    25+
                  </div>
                  <div className="text-sm text-gray-400">players</div>
                  <div className="text-lg font-semibold text-cresento-green-400">
                    25% off
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cresento-green-400 mb-2">
                    50+
                  </div>
                  <div className="text-sm text-gray-400">players</div>
                  <div className="text-lg font-semibold text-cresento-green-400">
                    35% off
                  </div>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-cresento-green-600 hover:bg-cresento-green-700 text-white font-bold py-4 px-8 rounded-full text-lg transition-colors"
              >
                Contact Sales Team
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-cresento-primary/20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-white"
          >
            Frequently Asked Questions
          </motion.h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-cresento-primary/30 border border-cresento-secondary/30 rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-lg font-semibold mb-3 text-white">
                  {faq.question}
                </h3>
                <p className="text-gray-300">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
